/**
 * DinheiroPublico — Funcionalidade 4 (Fase 3)
 *
 * Federal spending overview by state. Uses the Portal da Transparência via
 * the /api/public/gastos endpoint. Gracefully falls back to a friendly
 * "em preparação" message when the API key is absent.
 */
import React, { useState } from "react";
import ExplainButton from "../components/ExplainButton";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB",
  "PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const brl = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

export default function DinheiroPublico() {
  const currentYear = new Date().getFullYear();
  const [uf, setUf] = useState("PE");
  const [ano, setAno] = useState(currentYear - 1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const buscar = async () => {
    setLoading(true); setErr(null); setResult(null);
    try {
      const r = await fetch(`${BACKEND}/api/public/gastos?uf=${uf}&ano=${ano}`);
      if (!r.ok) throw new Error("network");
      const j = await r.json();
      setResult(j);
    } catch { setErr("network"); }
    finally { setLoading(false); }
  };

  const shareWhatsApp = () => {
    if (!result) return;
    const site = typeof window !== "undefined" ? window.location.origin : BACKEND;
    const texto = result.unavailable
      ? `Consultei os gastos públicos de ${result.uf} no Tô de Olho. Em breve com todos os dados oficiais.`
      : `💰 Em ${result.ano}, o governo federal gastou ${brl(result.total)} em contratos no estado de ${result.uf}.`;
    const text = `${texto}\nSaiba mais no Tô de Olho:\n${site}/dinheiro-publico`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="dinheiro-publico-page">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-[#1E3A5F] mb-2">Para Onde Foi o Dinheiro?</p>
        <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-1">Gastos públicos federais</h1>
        <p className="text-sm text-slate-600">Descubra quais empresas mais receberam dinheiro público em cada estado.</p>
      </header>

      <section className="bg-white rounded-2xl shadow-md p-6 mb-6">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Estado</label>
            <select
              value={uf}
              onChange={(e) => setUf(e.target.value)}
              className="w-full px-3 py-3 border border-slate-300 rounded-lg min-h-[44px] focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/30"
              data-testid="gastos-uf-select"
            >
              {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 block">Ano</label>
            <select
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              className="w-full px-3 py-3 border border-slate-300 rounded-lg min-h-[44px]"
              data-testid="gastos-ano-select"
            >
              {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={buscar}
              disabled={loading}
              className="w-full bg-[#1E3A5F] hover:bg-[#0F1C2E] disabled:bg-slate-400 text-white font-semibold py-3 rounded-lg min-h-[44px]"
              data-testid="gastos-buscar-btn"
            >
              {loading ? "Consultando…" : "Consultar"}
            </button>
          </div>
        </div>
      </section>

      {err && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm" data-testid="gastos-error">
          Ops! Não conseguimos consultar agora. Tente em alguns instantes.
        </div>
      )}

      {result && !loading && (
        <>
          <section className="bg-white rounded-2xl shadow-md overflow-hidden mb-6" data-testid="gastos-result">
            <div className="bg-gradient-to-br from-[#1E3A5F] to-[#0F1C2E] p-6 text-white">
              <p className="text-xs uppercase tracking-widest text-white/70 mb-1">{result.uf} · {result.ano}</p>
              {result.unavailable ? (
                <p className="text-lg font-semibold text-amber-200" data-testid="gastos-unavailable">{result.message}</p>
              ) : (
                <>
                  <p className="text-sm text-white/70">Total em contratos federais</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-4xl font-black" data-testid="gastos-total">{brl(result.total)}</p>
                    <ExplainButton
                      contexto={`total de contratos federais no estado de ${result.uf} em ${result.ano}`}
                      valor={brl(result.total)}
                      tipo="valor"
                      fonte="Portal da Transparência"
                      entidade={result.uf}
                      testId="explain-gastos-total"
                    />
                  </div>
                </>
              )}
            </div>

            {!result.unavailable && result.top_empresas.length > 0 && (
              <div className="p-6 border-b border-slate-100" data-testid="gastos-top-empresas">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-3 font-semibold">Top 5 empresas que mais receberam</p>
                <ul className="space-y-2">
                  {result.top_empresas.map((e, i) => (
                    <li key={i} className="flex justify-between items-center text-sm py-1 border-b border-slate-50">
                      <span className="text-slate-800 mr-3 flex-1 min-w-0 truncate">{e.nome}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-semibold text-[#1E3A5F] whitespace-nowrap">{brl(e.valor)}</span>
                        <ExplainButton
                          contexto={`valor recebido pela empresa ${e.nome} em contratos federais no estado de ${result.uf} em ${result.ano}`}
                          valor={brl(e.valor)}
                          tipo="valor"
                          fonte="Portal da Transparência"
                          entidade={e.nome}
                          testId={`explain-empresa-${i}`}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!result.unavailable && result.por_categoria.length > 0 && (
              <div className="p-6" data-testid="gastos-categorias">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-3 font-semibold">Principais categorias</p>
                <ul className="space-y-2">
                  {result.por_categoria.map((c, i) => (
                    <li key={i} className="flex justify-between text-sm py-1 border-b border-slate-50">
                      <span className="text-slate-800 mr-3 flex-1 min-w-0 truncate">{c.categoria}</span>
                      <span className="font-semibold text-slate-700 whitespace-nowrap">{brl(c.valor)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <button
            type="button"
            onClick={shareWhatsApp}
            className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white font-bold py-3 rounded-lg min-h-[44px] transition-colors"
            data-testid="gastos-share-whatsapp"
          >
            Compartilhar no WhatsApp
          </button>
        </>
      )}
    </div>
  );
}
